import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import { Boxes, Drill } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { toast } from 'sonner';
import { PageHeader, LoadingState, StatusBadge, DataTable, type Column } from '@/components/common';
import { DetailLayout, DetailList, RailCard, StatStrip } from '@/components/common/DetailPanels';
import { yardApi, type YardJobKit, type YardJobKitLine } from '@/api/services/yard.api';
import { formatDate, formatNumber, toTitleCase } from '@/lib/utils';
import { getErrorMessage } from '@/api/client';

/**
 * One job kit — what was asked for, what has been picked, and what went out.
 *
 * A kit is assembled against a work order and travels to a well; the crew that
 * receives it and the yard that built it are not in the same place, which is
 * exactly when a link matters. It was a sheet on the kit list.
 */
export default function YardJobKitDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [kit, setKit] = useState<YardJobKit | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    yardApi.jobKits
      .getById(id)
      .then((data) => !cancelled && setKit(data))
      .catch((error) => {
        if (cancelled) return;
        toast.error(getErrorMessage(error));
        navigate('/yard/job-kits');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  if (loading) return <LoadingState variant="detail" />;
  if (!kit) return null;

  const lines = kit.lines ?? [];
  const totals = lines.reduce(
    (acc, l) => ({
      requested: acc.requested + l.requestedQty,
      picked: acc.picked + l.pickedQty,
      issued: acc.issued + l.issuedQty,
      returned: acc.returned + l.returnedQty,
    }),
    { requested: 0, picked: 0, issued: 0, returned: 0 }
  );

  const lineColumns: Column<YardJobKitLine>[] = [
    {
      key: 'item',
      header: 'Item',
      linkTo: (l) => `/inventory/${l.itemId}`,
      render: (l) => (
        <div>
          <p className="font-medium">{l.item?.name ?? 'Unknown item'}</p>
          <p className="text-xs text-muted-foreground">{l.item?.sku}</p>
        </div>
      ),
    },
    {
      key: 'requestedQty',
      header: 'Requested',
      sortable: true,
      className: 'text-right',
      render: (l) => (
        <span className="block text-right tabular-nums">
          {formatNumber(l.requestedQty)} {l.item?.unit}
        </span>
      ),
    },
    {
      key: 'pickedQty',
      header: 'Picked',
      sortable: true,
      className: 'text-right',
      render: (l) => (
        // Short-picked lines are the reason to read this table, so the gap is
        // coloured rather than left to be worked out by subtraction.
        <span
          className={`block text-right tabular-nums ${
            l.pickedQty < l.requestedQty ? 'text-status-warning-foreground' : ''
          }`}
        >
          {formatNumber(l.pickedQty)}
        </span>
      ),
    },
    {
      key: 'issuedQty',
      header: 'Issued',
      sortable: true,
      className: 'text-right',
      render: (l) => (
        <span className="block text-right tabular-nums">{formatNumber(l.issuedQty)}</span>
      ),
    },
    {
      key: 'returnedQty',
      header: 'Returned',
      sortable: true,
      className: 'text-right',
      render: (l) => (
        <span className="block text-right tabular-nums text-muted-foreground">
          {l.returnedQty ? formatNumber(l.returnedQty) : '—'}
        </span>
      ),
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Job Kits', href: '/yard/job-kits' },
            { label: kit.kitNumber },
          ]}
          title={kit.name}
          description={kit.kitNumber}
          titleAdornment={<StatusBadge status={kit.status} />}
        />
      </motion.div>

      <motion.div variants={fadeUp}>
        <DetailLayout
          rail={
            <>
              <RailCard title="Job" icon={<Drill className="h-4 w-4 text-muted-foreground" />}>
                <DetailList
                  items={[
                    { label: 'Well', value: kit.wellName, when: !!kit.wellName },
                    {
                      label: 'Job type',
                      value: kit.jobType ? (
                        <Badge variant="outline">{toTitleCase(kit.jobType)}</Badge>
                      ) : null,
                      when: !!kit.jobType,
                    },
                    {
                      label: 'Work order',
                      value: kit.workOrder ? (
                        <Link
                          to={`/work-orders/${kit.workOrderId}`}
                          className="underline-offset-4 hover:underline"
                        >
                          {kit.workOrder.workOrderNumber}
                        </Link>
                      ) : null,
                      when: !!kit.workOrder,
                    },
                    {
                      label: 'Required by',
                      value: kit.requiredDate ? formatDate(kit.requiredDate) : null,
                      when: !!kit.requiredDate,
                    },
                    {
                      label: 'Assigned to',
                      value: kit.assignedTo?.fullName,
                      when: !!kit.assignedTo,
                    },
                  ]}
                />
              </RailCard>

              <RailCard title="Contents" icon={<Boxes className="h-4 w-4 text-muted-foreground" />}>
                <DetailList
                  items={[
                    { label: 'Lines', value: lines.length },
                    { label: 'Requested', value: formatNumber(totals.requested) },
                    {
                      label: 'Short picked',
                      value: formatNumber(totals.requested - totals.picked),
                      tone: 'warning',
                      when: totals.picked < totals.requested && totals.picked > 0,
                    },
                    {
                      label: 'Returned',
                      value: formatNumber(totals.returned),
                      when: totals.returned > 0,
                    },
                    { label: 'Created', value: formatDate(kit.createdAt) },
                  ]}
                />
              </RailCard>
            </>
          }
          main={
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <h3 className="text-base font-semibold text-foreground">Kit items</h3>
                  <CardDescription>
                    What the kit calls for, and how far through the yard has got
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <StatStrip
                    stats={[
                      { label: 'Requested', value: formatNumber(totals.requested) },
                      {
                        label: 'Picked',
                        value: (
                          <span
                            className={
                              totals.picked < totals.requested
                                ? 'text-status-warning-foreground'
                                : undefined
                            }
                          >
                            {formatNumber(totals.picked)}
                          </span>
                        ),
                      },
                      { label: 'Issued', value: formatNumber(totals.issued) },
                    ]}
                  />
                  <DataTable<YardJobKitLine>
                    pagination
                    data={lines}
                    columns={lineColumns}
                    emptyTitle="No items in this kit"
                    emptyDescription="Add items to the kit from the job kits list."
                  />
                </CardContent>
              </Card>

              {kit.description && (
                <Card>
                  <CardHeader>
                    <h3 className="text-base font-semibold text-foreground">Description</h3>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{kit.description}</p>
                  </CardContent>
                </Card>
              )}

              {kit.notes && (
                <Card>
                  <CardHeader>
                    <h3 className="text-base font-semibold text-foreground">Notes</h3>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{kit.notes}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          }
        />
      </motion.div>
    </motion.main>
  );
}
